import { z } from 'zod';
import {
  BACKUP_FORMAT_VERSION,
  MAX_CONTAINERS,
  MAX_EVENTS,
  MAX_INCIDENT_RESOLUTIONS,
  MAX_IMPORT_BYTES,
  MAX_RECIPE_SHOPPING_LIST_ITEMS,
  MAX_RECIPE_SHOPPING_LISTS,
  MAX_TEMPERATURE_INCIDENTS,
  MAX_TEMPERATURE_READINGS,
  SCHEMA_VERSION,
  type AuditRecord,
  type BackupEnvelope,
  type FoodContainer,
  type InventoryEvent,
  type InventorySnapshot,
  type StorageNode,
} from '../domain/model';
import {
  calculateVisibleNumberReleaseAt,
  hasVisibleNumber,
  holdsVisibleNumber,
  VISIBLE_NUMBER_QUARANTINE,
} from '../domain/visible-number';
import { assertEventHistoryWithinLimit } from '../domain/event-limit';
import { assertRecipeShoppingLists } from '../domain/recipe-journey';
import {
  assertNoFreezerHorizonForInternalContainer,
  assertSameStorageFamily,
  assertTrackingMatchesEquipment,
  storagePolicyFor,
} from '../domain/storage-policy';

const id = z.string().min(1).max(100);
const idempotencyKey = z.string().min(1).max(240);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const dateTime = z.string().datetime({ offset: true });
const nullableDate = date.nullable();
const nullableDateTime = dateTime.nullable();

const horizonRuleSchema = z
  .object({
    id,
    label: z.string().min(1).max(120),
    durationDays: z.number().int().min(1).max(3_650).nullable(),
    sourceTitle: z.string().min(1).max(240),
    sourceUrl: z.string().url().nullable(),
    sourceCheckedOn: date,
    version: z.string().min(1).max(80),
    disclaimer: z.string().min(1).max(500),
  })
  .strict();

const rotationAlertsSchema = z
  .array(
    z
      .object({
        threshold: z.enum(['j-7', 'j-2', 'j', 'overdue']),
        triggeredOn: date,
        createdAt: dateTime,
        state: z.enum(['unread', 'read', 'handled']),
      })
      .strict(),
  )
  .max(4);

const containerV5Schema = z
  .object({
    id,
    householdId: id,
    serialNumber: z.number().int().positive(),
    numberAssignedAt: dateTime,
    numberReleasedAt: nullableDateTime,
    numberAssignmentKind: z.enum(['first', 'reused', 'restored', 'reassigned', 'legacy']),
    numberPolicyVersion: z.string().min(1).max(80),
    rotationAlerts: rotationAlertsSchema,
    name: z.string().min(1).max(160),
    categoryId: id,
    locationId: id,
    initialQuantity: z.number().positive().max(1_000_000).nullable(),
    remainingQuantity: z.number().min(0).max(1_000_000).nullable(),
    unit: z.enum(['g', 'kg', 'ml', 'l', 'portion', 'piece', 'contenant']).nullable(),
    frozenOn: date,
    manufacturerDateType: z.enum(['none', 'DLC', 'DDM']),
    manufacturerDate: nullableDate,
    qualityHorizonOn: nullableDate,
    horizonRule: horizonRuleSchema.nullable(),
    status: z.enum(['draft', 'present', 'partial', 'out', 'discarded', 'archived']),
    incidentStatus: z.enum(['none', 'unresolved', 'resolved']),
    incidentNote: z.string().max(500),
    auditFlag: z.enum(['ok', 'missing', 'to_confirm']),
    notes: z.string().max(2_000),
    version: z.number().int().positive(),
    createdAt: dateTime,
    updatedAt: dateTime,
  })
  .strict();

const containerSchema = containerV5Schema
  .omit({
    serialNumber: true,
    numberAssignedAt: true,
    numberAssignmentKind: true,
    numberPolicyVersion: true,
    frozenOn: true,
  })
  .extend({
    trackingMode: z.enum(['visible-number', 'internal-only']),
    barcode: z.string().regex(/^\d{8,14}$/u).nullable().optional(),
    serialNumber: z.number().int().positive().nullable(),
    numberAssignedAt: nullableDateTime,
    numberAssignmentKind: z
      .enum(['first', 'reused', 'restored', 'reassigned', 'legacy'])
      .nullable(),
    numberPolicyVersion: z.string().min(1).max(80).nullable(),
    storageStartedOn: date,
    dateEvidence: z
      .object({
        storageStartedOn: z.object({ source: z.literal('household'), certainty: z.enum(['known', 'estimated']) }).strict(),
        purchasedOn: z.object({ source: z.literal('household'), certainty: z.enum(['known', 'estimated']) }).strict().nullable(),
        openedOn: z.object({ source: z.literal('household'), certainty: z.enum(['known', 'estimated']) }).strict().nullable(),
        useFirstOn: z.object({ source: z.literal('household'), certainty: z.enum(['known', 'estimated']) }).strict().nullable(),
        manufacturerDate: z.object({ source: z.literal('packaging'), certainty: z.enum(['known', 'estimated']) }).strict().nullable(),
      })
      .strict()
      .optional(),
    purchasedOn: nullableDate.optional(),
    openedOn: nullableDate.optional(),
    useFirstOn: nullableDate.optional(),
  })
  .strict()
  .superRefine((container, context) => {
    if (container.dateEvidence == null) return;
    const pairs = [
      ['purchasedOn', container.purchasedOn, container.dateEvidence.purchasedOn],
      ['openedOn', container.openedOn, container.dateEvidence.openedOn],
      ['useFirstOn', container.useFirstOn, container.dateEvidence.useFirstOn],
      ['manufacturerDate', container.manufacturerDate, container.dateEvidence.manufacturerDate],
    ] as const;
    for (const [field, value, evidence] of pairs) {
      if ((value == null) !== (evidence == null)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: `Preuve de date incohérente : ${field}.`, path: ['dateEvidence', field] });
      }
    }
  });

const legacyContainerSchema = containerV5Schema
  .omit({
    numberAssignedAt: true,
    numberReleasedAt: true,
    numberAssignmentKind: true,
    numberPolicyVersion: true,
    rotationAlerts: true,
  })
  .strict();

const eventCoreSchema = z
  .object({
    id,
    idempotencyKey,
    householdId: id,
    containerId: id.nullable(),
    eventType: z.enum([
      'created_draft',
      'confirmed',
      'consumed_total',
      'consumed_partial',
      'moved',
      'discarded',
      'restored',
      'corrected',
      'incident_changed',
      'audit_missing',
      'audit_confirmed',
      'undo',
    ]),
    payload: z.unknown(),
    occurredAt: dateTime,
    deviceId: id,
    deviceSequence: z.number().int().nonnegative(),
    revertsEventId: id.nullable(),
  })
  .strict();

const eventSchema = eventCoreSchema
  .extend({
    payload: z
      .object({
        before: containerSchema.nullable(),
        after: containerSchema.nullable(),
        summary: z.string().min(1).max(500),
        reason: z.string().max(500).nullable(),
      })
      .strict(),
  })
  .strict();

const eventV5Schema = eventCoreSchema
  .extend({
    payload: z
      .object({
        before: containerV5Schema.nullable(),
        after: containerV5Schema.nullable(),
        summary: z.string().min(1).max(500),
        reason: z.string().max(500).nullable(),
      })
      .strict(),
  })
  .strict();

const legacyEventSchema = eventCoreSchema
  .extend({
    payload: z
      .object({
        before: legacyContainerSchema.nullable(),
        after: legacyContainerSchema.nullable(),
        summary: z.string().min(1).max(500),
        reason: z.string().max(500).nullable(),
      })
      .strict(),
  })
  .strict();

const snapshotV3Schema = z
  .object({
    meta: z
      .object({
        key: z.literal('app'),
        schemaVersion: z.number().int(),
        householdId: id.nullable(),
        deviceId: id,
        deviceSequence: z.number().int().nonnegative(),
        nextSerial: z.number().int().positive(),
        persistentStorage: z.enum(['unknown', 'granted', 'denied', 'unsupported']),
        demoSeededAt: nullableDateTime.optional(),
        createdAt: dateTime,
        updatedAt: dateTime,
      })
      .strict(),
    household: z
      .object({
        id,
        name: z.string().min(1).max(120),
        mode: z.literal('local'),
        locale: z.literal('fr-FR'),
        timezone: z.string().min(1).max(100),
        createdAt: dateTime,
      })
      .strict()
      .nullable(),
    freezers: z
      .array(
        z
          .object({
            id,
            householdId: id,
            name: z.string().min(1).max(120),
            equipmentKind: z.enum(['three-star', 'four-star', 'fridge', 'unknown']),
            declaredTemperatureC: z.number().min(-40).max(10).nullable(),
            archivedAt: nullableDateTime,
            createdAt: dateTime,
          })
          .strict(),
      )
      .max(100),
    locations: z
      .array(
        z
          .object({
            id,
            freezerId: id,
            parentId: id.nullable(),
            name: z.string().min(1).max(120),
            position: z.number().int().nonnegative(),
            archivedAt: nullableDateTime,
          })
          .strict(),
      )
      .max(1_000),
    containers: z.array(containerV5Schema).max(MAX_CONTAINERS),
    events: z.array(eventV5Schema).max(MAX_EVENTS),
    audits: z
      .array(
        z
          .object({
            id,
            householdId: id,
            freezerId: id,
            locationId: id,
            expectedCycleIds: z.array(id).max(MAX_CONTAINERS),
            seenCycleIds: z.array(id).max(MAX_CONTAINERS),
            missingCycleIds: z.array(id).max(MAX_CONTAINERS),
            expectedSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
            seenSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
            missingSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
            unknownSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
            completedAt: dateTime,
          })
          .strict(),
      )
      .max(2_000),
    preferences: z
      .object({
        householdId: id,
        exclusions: z.array(z.string().min(1).max(100)).max(100),
        safetyAcknowledged: z.boolean(),
        alertHorizonDays: z.number().int().min(1).max(365),
        lastLocationId: id.nullable(),
        createdAt: dateTime,
        updatedAt: dateTime,
      })
      .strict()
      .nullable(),
  })
  .strict();

const temperatureReadingSchema = z
  .object({
    id,
    householdId: id,
    freezerId: id,
    deviceLabel: z.string().min(1).max(120),
    recordedAt: dateTime,
    currentTemperatureC: z.number().min(-80).max(50).nullable(),
    minimumTemperatureC: z.number().min(-80).max(50).nullable(),
    maximumTemperatureC: z.number().min(-80).max(50),
    unit: z.literal('C'),
    quality: z.literal('manual'),
    provenance: z.literal('mechanical_min_max'),
    resetOn: nullableDate,
    resetCertainty: z.enum(['known', 'estimated', 'unknown']),
    note: z.string().max(500),
    idempotencyKey,
  })
  .strict();

const timeBoundarySchema = z
  .object({
    value: nullableDateTime,
    certainty: z.enum(['known', 'estimated', 'unknown']),
  })
  .strict();

const temperatureIncidentSchema = z
  .object({
    id,
    idempotencyKey,
    householdId: id,
    freezerId: id,
    kind: z.enum(['warming', 'power_outage', 'door_open', 'equipment_failure', 'unknown']),
    source: z.enum([
      'manual_observation',
      'mechanical_min_max',
      'freezer_alarm',
      'known_power_outage',
      'sensor_history',
    ]),
    started: timeBoundarySchema,
    ended: timeBoundarySchema,
    maximumTemperatureC: z.number().min(-80).max(50).nullable(),
    note: z.string().max(2_000),
    affectedContainerIds: z.array(id).max(MAX_CONTAINERS),
    status: z.enum(['open', 'partially_resolved', 'resolved']),
    createdAt: dateTime,
    updatedAt: dateTime,
  })
  .strict();

const incidentResolutionSchema = z
  .object({
    id,
    idempotencyKey,
    householdId: id,
    incidentId: id,
    containerId: id,
    authorLocal: z.string().min(1).max(120),
    decidedAt: dateTime,
    choice: z.enum(['cleared', 'keep_reviewing']),
    comment: z.string().max(1_000),
  })
  .strict();

const snapshotV5Schema = snapshotV3Schema
  .extend({
    temperatureReadings: z.array(temperatureReadingSchema).max(MAX_TEMPERATURE_READINGS),
    temperatureIncidents: z.array(temperatureIncidentSchema).max(MAX_TEMPERATURE_INCIDENTS),
    incidentResolutions: z.array(incidentResolutionSchema).max(MAX_INCIDENT_RESOLUTIONS),
  })
  .strict();

const recipeShoppingListItemSchema = z
  .object({
    id,
    kind: z.enum(['stock', 'pantry']),
    ingredientId: id,
    label: z.string().min(1).max(160),
    requiredQuantity: z.number().positive().max(1_000_000).nullable(),
    unit: z.enum(['g', 'kg', 'ml', 'l', 'portion', 'piece', 'contenant']).nullable(),
    checked: z.boolean(),
  })
  .strict();

const recipeShoppingListHandoffReceiptSchema = z
  .object({
    target: z.enum(['archibald', 'paniprix']),
    intentId: z.string().regex(/^hnd_[A-Za-z0-9_-]{16,124}$/u),
    status: z.literal('accepted'),
    signatureVerified: z.literal(true),
    sourceMayRelease: z.literal(true),
    verifiedAt: dateTime,
  })
  .strict();

const currentPreferencesSchema = z
  .object({
    householdId: id,
    exclusions: z.array(z.string().min(1).max(100)).max(100),
    safetyAcknowledged: z.boolean(),
    alertHorizonDays: z.number().int().min(1).max(365),
    lastLocationId: id.nullable(),
    reminderFrequency: z.enum(['off', 'daily', 'weekly']).optional().default('off'),
    reminderRecipient: z.enum(['in-app', 'this-device']).optional().default('in-app'),
    lastReminderOn: nullableDate.optional().default(null),
    recipeShoppingLists: z
      .array(
        z
          .object({
            id,
            householdId: id,
            publicRecipeId: id,
            recipeTitle: z.string().min(1).max(160),
            locale: z.enum(['fr-FR', 'en']),
            portions: z.number().int().min(1).max(12),
            sourceReturnDigest: z.string().regex(/^[a-f0-9]{64}$/),
            items: z.array(recipeShoppingListItemSchema).max(MAX_RECIPE_SHOPPING_LIST_ITEMS),
            handoffReceipt: recipeShoppingListHandoffReceiptSchema.optional(),
            createdAt: dateTime,
            updatedAt: dateTime,
          })
          .strict(),
      )
      .max(MAX_RECIPE_SHOPPING_LISTS)
      .optional()
      .default([]),
    createdAt: dateTime,
    updatedAt: dateTime,
  })
  .strict()
  .nullable();

const snapshotSchema = snapshotV5Schema
  .extend({
    freezers: z
      .array(
        z
          .object({
            id,
            householdId: id,
            name: z.string().min(1).max(120),
            equipmentKind: z.enum(['three-star', 'four-star', 'fridge', 'pantry', 'unknown']),
            declaredTemperatureC: z.number().min(-40).max(10).nullable(),
            archivedAt: nullableDateTime,
            createdAt: dateTime,
          })
          .strict(),
      )
      .max(100),
    containers: z.array(containerSchema).max(MAX_CONTAINERS),
    events: z.array(eventSchema).max(MAX_EVENTS),
    preferences: currentPreferencesSchema,
  })
  .strict();

const legacyAuditSchema = z
  .object({
    id,
    householdId: id,
    freezerId: id,
    locationId: id,
    expectedSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
    seenSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
    missingSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
    unknownSerials: z.array(z.number().int().positive()).max(MAX_CONTAINERS),
    completedAt: dateTime,
  })
  .strict();

const legacySnapshotSchema = snapshotV3Schema
  .extend({
    containers: z.array(legacyContainerSchema).max(MAX_CONTAINERS),
    events: z.array(legacyEventSchema).max(MAX_EVENTS),
    audits: z.array(legacyAuditSchema).max(2_000).optional().default([]),
  })
  .strict();

const backupSchema = z
  .object({
    format: z.literal('inventaire-congelateurs'),
    formatVersion: z.literal(BACKUP_FORMAT_VERSION),
    schemaVersion: z.literal(SCHEMA_VERSION),
    exportedAt: dateTime,
    appVersion: z.string().min(1).max(40),
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    data: snapshotSchema,
  })
  .strict();

const schemaV4BackupSchema = z
  .object({
    format: z.literal('inventaire-congelateurs'),
    formatVersion: z.literal(BACKUP_FORMAT_VERSION),
    schemaVersion: z.literal(4),
    exportedAt: dateTime,
    appVersion: z.string().min(1).max(40),
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    data: snapshotV5Schema,
  })
  .strict();

const schemaV5BackupSchema = z
  .object({
    format: z.literal('inventaire-congelateurs'),
    formatVersion: z.literal(BACKUP_FORMAT_VERSION),
    schemaVersion: z.literal(5),
    exportedAt: dateTime,
    appVersion: z.string().min(1).max(40),
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    data: snapshotV5Schema,
  })
  .strict();

const legacyBackupSchema = z
  .object({
    format: z.literal('inventaire-congelateurs'),
    schemaVersion: z.union([z.literal(1), z.literal(2)]),
    exportedAt: dateTime,
    appVersion: z.string().min(1).max(40),
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    data: legacySnapshotSchema,
  })
  .strict();

const previousBackupSchema = z
  .object({
    format: z.literal('inventaire-congelateurs'),
    formatVersion: z.literal(2),
    schemaVersion: z.literal(3),
    exportedAt: dateTime,
    appVersion: z.string().min(1).max(40),
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    data: snapshotV3Schema,
  })
  .strict();

const sha256 = async (value: string): Promise<string> => {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

type LegacyContainer = z.infer<typeof legacyContainerSchema>;
type LegacySnapshot = z.infer<typeof legacySnapshotSchema>;
type PreviousSnapshot = z.infer<typeof snapshotV3Schema>;
type V5Container = z.infer<typeof containerV5Schema>;
type V5Snapshot = z.infer<typeof snapshotV5Schema>;

const addTemperatureHistory = (snapshot: PreviousSnapshot): V5Snapshot => {
  const freezerByLocation = new Map(
    snapshot.locations.map((location) => [location.id, location.freezerId]),
  );
  const unresolvedByFreezer = new Map<string, V5Container[]>();
  for (const container of snapshot.containers) {
    if (container.incidentStatus !== 'unresolved') continue;
    const freezerId = freezerByLocation.get(container.locationId);
    if (freezerId == null) continue;
    const list = unresolvedByFreezer.get(freezerId) ?? [];
    list.push(container);
    unresolvedByFreezer.set(freezerId, list);
  }
  return {
    ...snapshot,
    meta: {
      ...snapshot.meta,
      schemaVersion: SCHEMA_VERSION,
      demoSeededAt: snapshot.meta.demoSeededAt ?? null,
    },
    temperatureReadings: [],
    temperatureIncidents: [...unresolvedByFreezer.entries()].map(([freezerId, containers]) => {
      const createdAt = containers.map((container) => container.updatedAt).sort()[0]!;
      return {
        id: `migration-v4-${freezerId}`,
        idempotencyKey: `migration-v4-${freezerId}`,
        householdId: snapshot.household?.id ?? containers[0]!.householdId,
        freezerId,
        kind: 'unknown' as const,
        source: 'manual_observation' as const,
        started: { value: null, certainty: 'unknown' as const },
        ended: { value: null, certainty: 'unknown' as const },
        maximumTemperatureC: null,
        note:
          containers.map((container) => container.incidentNote.trim()).filter(Boolean).join(' · ') ||
          'Incident antérieur migré depuis une fiche de contenant.',
        affectedContainerIds: containers.map((container) => container.id).sort(),
        status: 'open' as const,
        createdAt,
        updatedAt: createdAt,
      };
    }),
    incidentResolutions: [],
  };
};

const migrateLegacyContainer = (container: LegacyContainer): V5Container => ({
  ...container,
  numberAssignedAt: container.createdAt,
  numberReleasedAt: ['draft', 'present', 'partial'].includes(container.status)
    ? null
    : calculateVisibleNumberReleaseAt(container.updatedAt),
  numberAssignmentKind: 'legacy',
  numberPolicyVersion: VISIBLE_NUMBER_QUARANTINE.version,
  rotationAlerts: [],
});

const migrateLegacySnapshot = (snapshot: LegacySnapshot): V5Snapshot => {
  const containers = snapshot.containers.map(migrateLegacyContainer);
  const cycleIdsFor = (serials: number[]): string[] =>
    serials.flatMap((serialNumber) => {
      const match = containers.find((container) => container.serialNumber === serialNumber);
      return match == null ? [] : [match.id];
    });
  return addTemperatureHistory({
    ...snapshot,
    containers,
    events: snapshot.events.map((event) => ({
      ...event,
      payload: {
        ...event.payload,
        before:
          event.payload.before == null ? null : migrateLegacyContainer(event.payload.before),
        after:
          event.payload.after == null ? null : migrateLegacyContainer(event.payload.after),
      },
    })),
    audits: snapshot.audits.map((audit) => ({
      ...audit,
      expectedCycleIds: cycleIdsFor(audit.expectedSerials),
      seenCycleIds: cycleIdsFor(audit.seenSerials),
      missingCycleIds: cycleIdsFor(audit.missingSerials),
    })),
    preferences:
      snapshot.preferences == null
        ? null
        : {
            ...snapshot.preferences,
            alertHorizonDays: 7,
          },
  });
};

const migrateV5Container = (
  container: V5Container,
  fridgeLocationIds: ReadonlySet<string>,
): FoodContainer => {
  const { frozenOn, ...rest } = container;
  if (fridgeLocationIds.has(container.locationId)) {
    const removedLegacyHorizon =
      container.horizonRule != null || container.qualityHorizonOn != null;
    return {
      ...rest,
      trackingMode: 'internal-only',
      serialNumber: null,
      numberAssignedAt: null,
      numberReleasedAt: null,
      numberAssignmentKind: null,
      numberPolicyVersion: null,
      rotationAlerts: [],
      storageStartedOn: frozenOn,
      qualityHorizonOn: null,
      horizonRule: null,
      notes:
        removedLegacyHorizon &&
        !container.notes.includes('Repère de congélation historique retiré')
          ? [container.notes, 'Repère de congélation historique retiré lors de la migration frigo.']
              .filter(Boolean)
              .join('\n')
          : container.notes,
    };
  }
  return {
    ...rest,
    trackingMode: 'visible-number',
    storageStartedOn: frozenOn,
  };
};

const migrateV5Snapshot = (snapshot: V5Snapshot): InventorySnapshot => {
  const fridgeIds = new Set(
    snapshot.freezers
      .filter((appliance) => appliance.equipmentKind === 'fridge')
      .map((appliance) => appliance.id),
  );
  const fridgeLocationIds = new Set(
    snapshot.locations
      .filter((location) => fridgeIds.has(location.freezerId))
      .map((location) => location.id),
  );
  const containers = snapshot.containers.map((container) =>
    migrateV5Container(container, fridgeLocationIds),
  );
  const numberedCycleIds = new Set(
    containers.filter(hasVisibleNumber).map((container) => container.id),
  );
  const keepAuditPair = (cycleIds: string[], serials: number[]) => {
    const kept = cycleIds.flatMap((cycleId, index) =>
      numberedCycleIds.has(cycleId) && serials[index] != null
        ? [{ cycleId, serialNumber: serials[index] }]
        : [],
    );
    return {
      cycleIds: kept.map((item) => item.cycleId),
      serials: kept.map((item) => item.serialNumber),
    };
  };
  return {
    ...snapshot,
    meta: {
      ...snapshot.meta,
      schemaVersion: SCHEMA_VERSION,
      demoSeededAt: snapshot.meta.demoSeededAt ?? null,
    },
    containers,
    events: snapshot.events.map((event) => ({
      ...event,
      payload: {
        ...event.payload,
        before:
          event.payload.before == null
            ? null
            : migrateV5Container(event.payload.before, fridgeLocationIds),
        after:
          event.payload.after == null
            ? null
            : migrateV5Container(event.payload.after, fridgeLocationIds),
      },
    })),
    audits: snapshot.audits.flatMap((audit) => {
      if (fridgeIds.has(audit.freezerId)) return [];
      const expected = keepAuditPair(audit.expectedCycleIds, audit.expectedSerials);
      const seen = keepAuditPair(audit.seenCycleIds, audit.seenSerials);
      const missing = keepAuditPair(audit.missingCycleIds, audit.missingSerials);
      return [
        {
          ...audit,
          expectedCycleIds: expected.cycleIds,
          expectedSerials: expected.serials,
          seenCycleIds: seen.cycleIds,
          seenSerials: seen.serials,
          missingCycleIds: missing.cycleIds,
          missingSerials: missing.serials,
        },
      ];
    }),
  };
};

export const cycleMatchesAuditHistory = (
  container: FoodContainer,
  audit: AuditRecord,
  serialNumber: number,
  events: InventoryEvent[],
  locations: StorageNode[],
): boolean => {
  if (!hasVisibleNumber(container)) {
    return false;
  }
  const locationsById = new Map(locations.map((location) => [location.id, location]));
  const auditEventKeys = new Set([
    `audit-confirm:${audit.id}:${container.id}`,
    `audit-missing:${audit.id}:${container.id}`,
  ]);
  const anchoredState = events.find(
    (event) =>
      event.containerId === container.id &&
      auditEventKeys.has(event.idempotencyKey) &&
      ['audit_confirmed', 'audit_missing'].includes(event.eventType) &&
      event.occurredAt <= audit.completedAt,
  )?.payload.after;
  if (anchoredState != null) {
    return (
      hasVisibleNumber(anchoredState) &&
      anchoredState.id === container.id &&
      anchoredState.serialNumber === serialNumber &&
      anchoredState.locationId === audit.locationId &&
      locationsById.get(anchoredState.locationId)?.freezerId === audit.freezerId
    );
  }
  const historicalEvents = events
    .filter(
      (event) =>
        event.containerId === container.id &&
        event.occurredAt <= audit.completedAt,
    )
    .sort(
      (left, right) =>
        left.occurredAt.localeCompare(right.occurredAt) ||
        left.deviceSequence - right.deviceSequence ||
        left.id.localeCompare(right.id),
    );
  const historicalState = historicalEvents.at(-1)?.payload.after ?? null;
  if (historicalState != null) {
    return (
      hasVisibleNumber(historicalState) &&
      historicalState.id === container.id &&
      historicalState.serialNumber === serialNumber &&
      historicalState.locationId === audit.locationId &&
      locationsById.get(historicalState.locationId)?.freezerId === audit.freezerId
    );
  }
  return (
    container.serialNumber === serialNumber &&
    container.createdAt <= audit.completedAt &&
    container.updatedAt <= audit.completedAt &&
    container.locationId === audit.locationId &&
    locationsById.get(container.locationId)?.freezerId === audit.freezerId
  );
};

const checkRelationships = (snapshot: InventorySnapshot): void => {
  if (snapshot.meta.schemaVersion !== SCHEMA_VERSION) {
    throw new Error(`Le schéma de données doit être en version ${SCHEMA_VERSION}.`);
  }
  if (snapshot.household == null) {
    throw new Error('La sauvegarde ne contient aucun foyer.');
  }
  const householdId = snapshot.household.id;
  if (snapshot.meta.householdId !== householdId) {
    throw new Error('Le foyer actif ne correspond pas aux données exportées.');
  }
  const freezerIds = new Set(snapshot.freezers.map((freezer) => freezer.id));
  const freezersById = new Map(snapshot.freezers.map((freezer) => [freezer.id, freezer]));
  const locationsById = new Map(snapshot.locations.map((location) => [location.id, location]));
  const locationIds = new Set(snapshot.locations.map((location) => location.id));
  const containerIds = new Set(snapshot.containers.map((container) => container.id));
  const containersById = new Map(snapshot.containers.map((container) => [container.id, container]));
  const activeSerials = new Set<number>();

  if (snapshot.preferences != null) {
    if (snapshot.preferences.householdId !== householdId) {
      throw new Error('Les préférences appartiennent à un autre foyer.');
    }
    assertRecipeShoppingLists(
      householdId,
      snapshot.preferences.recipeShoppingLists ?? [],
    );
  }

  for (const freezer of snapshot.freezers) {
    if (freezer.householdId !== householdId) {
      throw new Error('Un appareil appartient à un autre foyer.');
    }
  }
  for (const location of snapshot.locations) {
    if (!freezerIds.has(location.freezerId)) {
      throw new Error('Un emplacement référence un appareil absent.');
    }
    const visited = new Set([location.id]);
    let depth = 1;
    let cursor = location;
    while (cursor.parentId != null) {
      if (visited.has(cursor.parentId)) {
        throw new Error('Un cycle est interdit dans la hiérarchie des emplacements.');
      }
      visited.add(cursor.parentId);
      const parent = locationsById.get(cursor.parentId);
      if (parent == null) {
        throw new Error('Un emplacement parent est absent.');
      }
      if (parent.freezerId !== location.freezerId) {
        throw new Error('Un emplacement et son parent doivent appartenir au même appareil.');
      }
      depth += 1;
      if (depth > 3) {
        throw new Error('Trois niveaux maximum sont autorisés sous un appareil.');
      }
      cursor = parent;
    }
  }
  for (const container of snapshot.containers) {
    if (container.householdId !== householdId || !locationIds.has(container.locationId)) {
      throw new Error('Un contenant référence un foyer ou un emplacement absent.');
    }
    const appliance = freezersById.get(locationsById.get(container.locationId)!.freezerId);
    if (appliance == null) {
      throw new Error('Un contenant référence un appareil absent.');
    }
    assertTrackingMatchesEquipment(container, appliance.equipmentKind);
    if (
      container.trackingMode === 'internal-only' &&
      (container.numberAssignedAt != null ||
        container.numberReleasedAt != null ||
        container.numberAssignmentKind != null ||
        container.numberPolicyVersion != null)
    ) {
      throw new Error('Un aliment stocké sans numéro contient des métadonnées de numéro visibles.');
    }
    if (holdsVisibleNumber(container) && activeSerials.has(container.serialNumber)) {
      throw new Error(
        `Le numéro actif ${container.serialNumber} apparaît sur plusieurs contenants.`,
      );
    }
    if (holdsVisibleNumber(container)) {
      activeSerials.add(container.serialNumber);
    }
    if (holdsVisibleNumber(container) && container.numberReleasedAt != null) {
      throw new Error(`Le numéro actif ${container.serialNumber} est marqué comme libéré.`);
    }
    if (
      hasVisibleNumber(container) &&
      container.numberPolicyVersion !== VISIBLE_NUMBER_QUARANTINE.version
    ) {
      throw new Error('La politique de quarantaine des numéros est inconnue.');
    }
    if (
      hasVisibleNumber(container) &&
      !['draft', 'present', 'partial'].includes(container.status) &&
      container.numberReleasedAt == null
    ) {
      throw new Error(`Le numéro inactif ${container.serialNumber} n’a pas de date de libération.`);
    }
    if (
      container.numberAssignedAt != null &&
      container.numberReleasedAt != null &&
      container.numberReleasedAt < container.numberAssignedAt
    ) {
      throw new Error(`Les dates d’attribution du numéro ${container.serialNumber} sont incohérentes.`);
    }
  }
  for (const event of snapshot.events) {
    if (event.householdId !== householdId) {
      throw new Error('Un événement appartient à un autre foyer.');
    }
    if (event.containerId != null && !containerIds.has(event.containerId)) {
      throw new Error('Un événement référence un contenant absent.');
    }
    for (const state of [event.payload.before, event.payload.after]) {
      if (
        state != null &&
        (state.id !== event.containerId || state.householdId !== householdId)
      ) {
        throw new Error('Un événement mélange les historiques de deux contenants.');
      }
      if (state != null) {
        const location = locationsById.get(state.locationId);
        const appliance = location == null ? null : freezersById.get(location.freezerId);
        if (appliance == null) {
          throw new Error('Un événement contient un mode d’identification incompatible.');
        }
        assertTrackingMatchesEquipment(state, appliance.equipmentKind);
      }
    }
    if (event.payload.before != null && event.payload.after != null) {
      const sourceLocation = locationsById.get(event.payload.before.locationId);
      const destinationLocation = locationsById.get(event.payload.after.locationId);
      const sourceAppliance = sourceLocation == null ? null : freezersById.get(sourceLocation.freezerId);
      const destinationAppliance = destinationLocation == null
        ? null
        : freezersById.get(destinationLocation.freezerId);
      if (sourceAppliance == null || destinationAppliance == null) {
        throw new Error('Un événement référence un appareil absent.');
      }
      assertSameStorageFamily(sourceAppliance.equipmentKind, destinationAppliance.equipmentKind);
    }
  }
  for (const audit of snapshot.audits) {
    const freezer = freezersById.get(audit.freezerId);
    const location = locationsById.get(audit.locationId);
    if (
      audit.householdId !== householdId ||
      freezer == null ||
      location == null ||
      location.freezerId !== freezer.id
    ) {
      throw new Error('Un audit référence un foyer, un appareil ou un emplacement absent.');
    }
    if (storagePolicyFor(freezer.equipmentKind).trackingMode !== 'visible-number') {
      throw new Error('Un audit par numéro ne peut pas cibler un stockage sans numéro.');
    }
    const pairs = [
      [audit.expectedCycleIds, audit.expectedSerials],
      [audit.seenCycleIds, audit.seenSerials],
      [audit.missingCycleIds, audit.missingSerials],
    ] as const;
    for (const [cycleIds, serials] of pairs) {
      if (
        cycleIds.length !== serials.length ||
        cycleIds.some(
          (cycleId, index) => {
            const container = containersById.get(cycleId);
            if (container == null) return true;
            return !cycleMatchesAuditHistory(
              container,
              audit,
              serials[index]!,
              snapshot.events,
              snapshot.locations,
            );
          },
        )
      ) {
        throw new Error('Un audit mélange les numéros de plusieurs cycles de contenant.');
      }
    }
  }
  const incidentIds = new Set(snapshot.temperatureIncidents.map((incident) => incident.id));
  for (const reading of snapshot.temperatureReadings) {
    if (
      reading.householdId !== householdId ||
      !freezerIds.has(reading.freezerId) ||
      storagePolicyFor(freezersById.get(reading.freezerId)!.equipmentKind).family === 'pantry' ||
      (reading.resetCertainty === 'unknown') !== (reading.resetOn == null)
    ) {
      throw new Error('Un relevé de température contient une relation ou une date incohérente.');
    }
    if (
      reading.minimumTemperatureC != null &&
      reading.minimumTemperatureC > reading.maximumTemperatureC
    ) {
      throw new Error('Un relevé de température a un minimum supérieur au maximum.');
    }
  }
  for (const incident of snapshot.temperatureIncidents) {
    if (
      incident.householdId !== householdId ||
      !freezerIds.has(incident.freezerId) ||
      storagePolicyFor(freezersById.get(incident.freezerId)!.equipmentKind).family === 'pantry' ||
      incident.affectedContainerIds.some((containerId) => !containerIds.has(containerId))
    ) {
      throw new Error('Un incident de température référence une donnée absente.');
    }
    for (const boundary of [incident.started, incident.ended]) {
      if ((boundary.certainty === 'unknown') !== (boundary.value == null)) {
        throw new Error('Une borne temporelle d’incident est incohérente.');
      }
    }
    if (
      incident.started.value != null &&
      incident.ended.value != null &&
      incident.started.value > incident.ended.value
    ) {
      throw new Error('Les bornes temporelles d’un incident sont inversées.');
    }
  }
  for (const resolution of snapshot.incidentResolutions) {
    if (
      resolution.householdId !== householdId ||
      !incidentIds.has(resolution.incidentId) ||
      !containerIds.has(resolution.containerId) ||
      !snapshot.temperatureIncidents
        .find((incident) => incident.id === resolution.incidentId)
        ?.affectedContainerIds.includes(resolution.containerId)
    ) {
      throw new Error('Une décision d’incident référence une exposition absente.');
    }
  }
};

export const validateInventorySnapshotCandidate = (
  snapshot: InventorySnapshot,
  options: { allowEmptyHousehold?: boolean } = {},
): InventorySnapshot => {
  assertEventHistoryWithinLimit(snapshot.events.length);
  const parsed = snapshotSchema.parse(structuredClone(snapshot));
  if (options.allowEmptyHousehold && parsed.household == null) {
    if (
      parsed.meta.householdId != null ||
      parsed.freezers.length > 0 ||
      parsed.locations.length > 0 ||
      parsed.containers.length > 0 ||
      parsed.events.length > 0 ||
      parsed.audits.length > 0 ||
      parsed.temperatureReadings.length > 0 ||
      parsed.temperatureIncidents.length > 0 ||
      parsed.incidentResolutions.length > 0 ||
      parsed.preferences != null
    ) {
      throw new Error('Une base sans foyer contient encore des données métier.');
    }
    return parsed;
  }
  checkRelationships(parsed);
  return parsed;
};

export const createBackup = async (
  snapshot: InventorySnapshot,
  appVersion: string,
): Promise<BackupEnvelope> => {
  assertEventHistoryWithinLimit(snapshot.events.length);
  const exportedAt = new Date().toISOString();
  const candidate: InventorySnapshot = {
    ...structuredClone(snapshot),
    meta: {
      ...structuredClone(snapshot.meta),
      schemaVersion: SCHEMA_VERSION,
      demoSeededAt: snapshot.meta.demoSeededAt ?? null,
    },
  };
  const data = validateInventorySnapshotCandidate(candidate);
  const checksum = await sha256(JSON.stringify(data));
  return {
    format: 'inventaire-congelateurs',
    formatVersion: BACKUP_FORMAT_VERSION,
    schemaVersion: SCHEMA_VERSION,
    exportedAt,
    appVersion,
    checksum,
    data,
  };
};

export const parseBackup = async (raw: string): Promise<BackupEnvelope> => {
  if (new TextEncoder().encode(raw).byteLength > MAX_IMPORT_BYTES) {
    throw new Error('Le fichier dépasse la limite de 5 Mo.');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Le fichier JSON est illisible.');
  }
  const currentResult = backupSchema.safeParse(parsed);
  const schemaV5Result = currentResult.success ? null : schemaV5BackupSchema.safeParse(parsed);
  const schemaV4Result =
    currentResult.success || schemaV5Result?.success
      ? null
      : schemaV4BackupSchema.safeParse(parsed);
  const previousResult =
    currentResult.success || schemaV5Result?.success || schemaV4Result?.success
      ? null
      : previousBackupSchema.safeParse(parsed);
  const legacyResult =
    currentResult.success ||
    schemaV5Result?.success ||
    schemaV4Result?.success ||
    previousResult?.success
      ? null
      : legacyBackupSchema.safeParse(parsed);
  if (
    !currentResult.success &&
    (schemaV5Result == null || !schemaV5Result.success) &&
    (schemaV4Result == null || !schemaV4Result.success) &&
    (previousResult == null || !previousResult.success) &&
    (legacyResult == null || !legacyResult.success)
  ) {
    const issue =
      currentResult.error.issues[0] ??
      (schemaV5Result != null && !schemaV5Result.success
        ? schemaV5Result.error.issues[0]
        : null) ??
      (schemaV4Result != null && !schemaV4Result.success
        ? schemaV4Result.error.issues[0]
        : null) ??
      (previousResult != null && !previousResult.success ? previousResult.error.issues[0] : null) ??
      (legacyResult != null && !legacyResult.success ? legacyResult.error.issues[0] : null);
    throw new Error(
      `Sauvegarde invalide : ${issue?.path.join('.') || 'structure'} ${issue?.message ?? ''}`.trim(),
    );
  }
  if (currentResult.success) {
    const actualChecksum = await sha256(JSON.stringify(currentResult.data.data));
    if (actualChecksum !== currentResult.data.checksum) {
      throw new Error('L’empreinte de la sauvegarde ne correspond pas : fichier altéré ou incomplet.');
    }
    checkRelationships(currentResult.data.data);
    return currentResult.data;
  }
  if (schemaV5Result?.success) {
    const rawData = (parsed as { data: unknown }).data;
    const actualChecksum = await sha256(JSON.stringify(rawData));
    if (actualChecksum !== schemaV5Result.data.checksum) {
      throw new Error('L’empreinte de la sauvegarde ne correspond pas : fichier altéré ou incomplet.');
    }
    const migratedData = migrateV5Snapshot(schemaV5Result.data.data);
    assertEventHistoryWithinLimit(migratedData.events.length);
    checkRelationships(migratedData);
    return {
      format: 'inventaire-congelateurs',
      formatVersion: BACKUP_FORMAT_VERSION,
      schemaVersion: SCHEMA_VERSION,
      exportedAt: schemaV5Result.data.exportedAt,
      appVersion: schemaV5Result.data.appVersion,
      checksum: await sha256(JSON.stringify(migratedData)),
      data: migratedData,
    };
  }
  if (schemaV4Result?.success) {
    const rawData = (parsed as { data: unknown }).data;
    const actualChecksum = await sha256(JSON.stringify(rawData));
    if (actualChecksum !== schemaV4Result.data.checksum) {
      throw new Error('L’empreinte de la sauvegarde ne correspond pas : fichier altéré ou incomplet.');
    }
    const migratedData = migrateV5Snapshot(schemaV4Result.data.data);
    assertEventHistoryWithinLimit(migratedData.events.length);
    checkRelationships(migratedData);
    return {
      format: 'inventaire-congelateurs',
      formatVersion: BACKUP_FORMAT_VERSION,
      schemaVersion: SCHEMA_VERSION,
      exportedAt: schemaV4Result.data.exportedAt,
      appVersion: schemaV4Result.data.appVersion,
      checksum: await sha256(JSON.stringify(migratedData)),
      data: migratedData,
    };
  }
  if (previousResult?.success) {
    const rawData = (parsed as { data: unknown }).data;
    const actualChecksum = await sha256(JSON.stringify(rawData));
    if (actualChecksum !== previousResult.data.checksum) {
      throw new Error('L’empreinte de la sauvegarde ne correspond pas : fichier altéré ou incomplet.');
    }
    const migratedData = migrateV5Snapshot(addTemperatureHistory(previousResult.data.data));
    assertEventHistoryWithinLimit(migratedData.events.length);
    checkRelationships(migratedData);
    return {
      format: 'inventaire-congelateurs',
      formatVersion: BACKUP_FORMAT_VERSION,
      schemaVersion: SCHEMA_VERSION,
      exportedAt: previousResult.data.exportedAt,
      appVersion: previousResult.data.appVersion,
      checksum: await sha256(JSON.stringify(migratedData)),
      data: migratedData,
    };
  }
  if (legacyResult == null || !legacyResult.success) {
    throw new Error('Sauvegarde historique invalide.');
  }
  const rawLegacyData = (parsed as { data: unknown }).data;
  const legacyChecksum = await sha256(JSON.stringify(rawLegacyData));
  if (legacyChecksum !== legacyResult.data.checksum) {
    throw new Error('L’empreinte de la sauvegarde ne correspond pas : fichier altéré ou incomplet.');
  }
  const migratedData = migrateV5Snapshot(migrateLegacySnapshot(legacyResult.data.data));
  assertEventHistoryWithinLimit(migratedData.events.length);
  checkRelationships(migratedData);
  return {
    format: 'inventaire-congelateurs',
    formatVersion: BACKUP_FORMAT_VERSION,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: legacyResult.data.exportedAt,
    appVersion: legacyResult.data.appVersion,
    checksum: await sha256(JSON.stringify(migratedData)),
    data: migratedData,
  };
};

const csvCell = (value: string | number | null | undefined): string => {
  const text = value == null ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
};

export const createCsv = (containers: FoodContainer[]): string => {
  for (const container of containers) {
    assertNoFreezerHorizonForInternalContainer(container);
  }
  const header = [
    'cycle_id',
    'mode_suivi',
    'numero',
    'numero_attribue_le',
    'numero_libere_le',
    'type_attribution_numero',
    'politique_numero',
    'nom',
    'code_barres',
    'categorie',
    'statut',
    'quantite_restante',
    'unite',
    'date_entree_stockage',
    'preuve_dates',
    'type_date_fabricant',
    'date_fabricant',
    'horizon_qualite',
    'alertes_rotation',
    'emplacement_id',
    'incident',
  ];
  const rows = [...containers]
    .sort(
      (left, right) =>
        (left.serialNumber ?? Number.MAX_SAFE_INTEGER) -
          (right.serialNumber ?? Number.MAX_SAFE_INTEGER) ||
        left.createdAt.localeCompare(right.createdAt),
    )
    .map((container) =>
      [
        container.id,
        container.trackingMode,
        container.serialNumber,
        container.numberAssignedAt,
        container.numberReleasedAt,
        container.numberAssignmentKind,
        container.numberPolicyVersion,
        container.name,
        container.barcode,
        container.categoryId,
        container.status,
        container.remainingQuantity,
        container.unit,
        container.storageStartedOn,
        JSON.stringify(container.dateEvidence ?? null),
        container.manufacturerDateType,
        container.manufacturerDate,
        container.qualityHorizonOn,
        container.rotationAlerts.map((alert) => `${alert.threshold}:${alert.state}`).join('|'),
        container.locationId,
        container.incidentStatus,
      ]
        .map(csvCell)
        .join(','),
    );
  return `\uFEFF${header.join(',')}\r\n${rows.join('\r\n')}\r\n`;
};

export const downloadTextFile = (filename: string, content: string, type: string): void => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};
